import Vapi from 'vuex-rest-api';
import AppEvents from '@/AppEvents';
import User from '@/models/User';
import { Organization } from '@/models/Organization';
import router from '@/router';
import Destination from '@/components/Passenger/models/Destination';
import { PassengerGroup } from '@/models/PassengerGroup';
import Passenger from '@/components/Passenger/models/Passenger';
import { IPassengerGroupPassenger, IPassengerGroupWithPassengers } from '@/typings/timit';
import { AuthTenant } from '@/models/AuthTenant';

interface IOrganizationState {
  organizations: Organization[];
  users: User[],
  passengerGroups: PassengerGroup[];
  selectedPassengerGroup: IPassengerGroupWithPassengers | null;
}

const organizationApi = new Vapi({
  // baseURL: process.env.VUE_APP_API_HOST + '/api/organization',
  baseURL: process.env.VUE_APP_API_HOST + '/api/',
  state: {
    organizations: [],
    users: [],
    passengerGroups: [],
    selectedPassengerGroup: null,
  }
}).get({
  action: 'getOrganizations',
  path: () => `organization`,
  onSuccess: (state, payload, axios) => {
    state.organizations = payload.data.map(p => Organization.fromApi(p));
    return state.organizations;
  },
  onError: (state: any, error: any, axios: any) => {
    AppEvents.handleApiError(error);
  },
}).get({
  action: 'getUsers',
  path: ({ id }) => `organization/${id}/users`,
  onSuccess: (state, payload, axios) => state.users = payload.data.map(p => User.fromApi(p)),
  onError: (state, error, axios) => AppEvents.handleApiError(error),
}).get({
  action: 'getCostcenters',
  path: ({ id }) => `organization/${id}/costcenter`,
  onSuccess: (state, payload, axios) => payload.data,
  onError: (state, error, axios) => AppEvents.handleApiError(error),
}).get({
  action: 'getPassengerGroups',
  path: ({ id }) => `organization/${id}/passengergroups`,
  onSuccess: (state, payload, axios) => {
    // state.passengerGroups = payload.data.map(p => PassengerGroup.fromApi(p))
    state.passengerGroups = payload.data;
  },
  onError: (state, error, axios) => AppEvents.handleApiError(error),
}).get({
  action: 'getPassengerGroupWithPassengers',
  path: ({ id }) => `passengergroup/${id}/withpassengers`,
  onSuccess: (state: IOrganizationState, payload, axios) => {
    state.selectedPassengerGroup = payload.data;
    return state.selectedPassengerGroup;
  },
  onError: (state, error, axios) => AppEvents.handleApiError(error),
}).get({
  action: 'getPtpNamesForOrganization',
  path: ({ id }) => `organization/${id}/passengerTranportationPermissionNames`,
  onSuccess: (state: IOrganizationState, payload, axios, data) => {
    const orgId = data.params.id;
    const target = state.organizations.find(o => o.id === orgId);

    if (target) {
      target.ptpNames = payload.data;
    }

    return payload.data;
  },
  onError: (state, error, axios) => AppEvents.handleApiError(error)
}).get({
  action: 'getCostCentersForOrganization',
  path: ({ id }) => `organization/${id}/costCenters`,
  onSuccess: (state: IOrganizationState, payload, axios, data) => {
    const orgId = data.params.id;
    const target = state.organizations.find(o => o.id === orgId);

    if (target) {
      target.costCenterNames = payload.data;
    }

    return payload.data;
  },
  onError: (state, error, axios) => AppEvents.handleApiError(error)
}).post({
  action: 'createPassengerGroup',
  property: 'createPassengerGroup',
  path: 'passengerGroup',
  onSuccess: (state, payload, axios) =>  {
    state.passengerGroups.push(payload.data)
  },
  onError: (state: any, error: any, axios: any) => AppEvents.handleApiError(error),
})
.post({
  action: 'createPassengerGroupPassenger',
  property: 'createPassengerGroupPassenger',
  path: ({ id }) => `passengerGroup/${id}/passenger`,
  onSuccess: (state: IOrganizationState, payload, axios) =>  {
    const passenger = new Passenger(payload.data);
    if (state.selectedPassengerGroup) {
      const passengerGroupPassenger = passengerToPassengerGroupPassenger(passenger);
      state.selectedPassengerGroup.passengerGroupPassengers.push(passengerGroupPassenger);
    }
    return passenger;
  },
  onError: (state: any, error: any, axios: any) => AppEvents.handleApiError(error),
})
.put({
  action: 'modifyPassengerGroupPassenger',
  property: 'createPassengerGroupPassenger',
  path: ({ id }) => `passengerGroup/${id}`,
  onSuccess: (state: IOrganizationState, payload, axios) =>  {
    const passenger = new Passenger(payload.data);
    if (state.selectedPassengerGroup) {
      const index = state.selectedPassengerGroup.passengerGroupPassengers.findIndex(p => p.id === passenger.id);
      if (index > -1) {
        const passengerGroupPassenger = passengerToPassengerGroupPassenger(passenger);
        state.selectedPassengerGroup.passengerGroupPassengers.splice(index, 1, passengerGroupPassenger);
      }
    }
    return passenger;
  },
  onError: (state: any, error: any, axios: any) => AppEvents.handleApiError(error),
})
.post({
  action: 'addPassengerToPassengerGroup',
  property: 'addPassengerToPassengerGroup',
  path: `passengerGroup/addpassenger`,
  onSuccess: (state: IOrganizationState, payload, axios) => {
    const addedPassengers = payload.data.map(p => new Passenger(p));
    if (state.selectedPassengerGroup) {
      const addedPassengerGroupPassengers = addedPassengers.map(p => passengerToPassengerGroupPassenger(p));
      state.selectedPassengerGroup.passengerGroupPassengers = state.selectedPassengerGroup.passengerGroupPassengers
        .concat(addedPassengerGroupPassengers);
    }
    return addedPassengers;
  },
  onError: (state: any, error: any, axios: any) => AppEvents.handleApiError(error),
})
.put({
  action: 'removePassengerFromPassengerGroup',
  property: 'removePassengerFromPassengerGroup',
  path: ({ id, passengerId }) => `passengerGroup/${id}/removepassenger/${passengerId}`,
  onSuccess: (state: IOrganizationState, payload, axios) => {
    // do nothing;
  },
  onError: (state: any, error: any, axios: any) => {
    AppEvents.handleApiError(error)
  },
})
.put({
  action: 'removeUser',
  property: 'removeUser',
  path: ({ id }) => `organization/${id}/users/remove`,
  onSuccess: (state: IOrganizationState, payload, axios) => {
    const removedUser = User.fromApi(payload.data);
    const userIndex = state.users.findIndex(u => u.id === removedUser.id);
    if (userIndex >= 0) {
      state.users.splice(userIndex, 1, removedUser);
    }
  },
  onError: (state: any, error: any, axios: any) => AppEvents.handleApiError(error),
})
.post({
  action: 'inviteUser',
  property: 'inviteUser',
  path: 'organization/invite',
  onSuccess: (state, payload, axios) =>  {
    const user = User.fromApi(payload.data);
    const index = (state.users as User[]).findIndex(u => u.email === user.email);
    if (index >= 0) {
      state.users.splice(index, 1, user);
    } else {
      state.users.push(user);
    }
  },
  onError: (state: any, error: any, axios: any) => AppEvents.handleApiError(error),
})
.post({
  action: 'createDestinationForOrganization',
  property: 'createDestinationForOrganization',
  path: `organization/destination`,
  onSuccess: (state, payload, axios) => {
    const destination: Destination = new Destination(payload.data);

    const organization = (state.organizations as Organization[])
      .find(o => o.id === router.currentRoute.params.organizationId);

    organization.destinations.push(destination);
    return destination;
  },
  onError: (state: any, error: any, axios: any) => AppEvents.handleApiError(error),
})
.post({
  action: 'createAuthTenant',
  property: 'createAuthTenant',
  path: `organization/authTenant`,
  onSuccess: (state, payload, axios) => {
    const authTenant: AuthTenant = AuthTenant.fromApi(payload.data);
    delete payload.data;
    const organization = (state.organizations as Organization[])
      .find(o => o.id === router.currentRoute.params.organizationId);
    organization.authTenants.push(authTenant);
    return authTenant;
  },
  onError: (state: any, error: any, axios: any) => AppEvents.handleApiError(error),
})
.get({
  action: 'getFinvoice',
  property: 'getFinvoice',
  path: ({id, costcenterId, startDate, endDate}) => `organization/${id}/costcenter/${costcenterId}?startDate=${startDate}&endDate=${endDate}`,
  onSuccess: (state, payload, axios) => payload.data,
  onError: (state: any, error: any, axios: any) => AppEvents.handleApiError(error),
})

const getters = {
  organizationPtpNames: (state: IOrganizationState) => (organizationId: string) => {
    const organization = state.organizations.find(o => o.id === organizationId);
    if (organization) {
      return organization.ptpNames;
    }
    return [];
  },
  organizationCostCenterNames: (state: IOrganizationState) => (organizationId: string) => {
    const organization = state.organizations.find(o => o.id === organizationId);
    if (organization) {
      return organization.costCenterNames;
    }
    return [];
  },
  organizationPtpDeductibleTypeIds: (state: IOrganizationState) => (organizationId: string) => {
    const organization = state.organizations.find(o => o.id === organizationId);
    if (organization) {
      return organization.ptpDeductibleTypeIds || '';
    }
    return '';
  },
  organizationRequirePtpByDefault: (state: IOrganizationState) => (organizationId: string) => {
    const organization = state.organizations.find(o => o.id === organizationId);
    if (organization) {
      return organization.requirePtpByDefault;
    }
    return false;
  },
  organizationSavePtpAsDraftByDefault: (state: IOrganizationState) => (organizationId: string) => {
    const organization = state.organizations.find(o => o.id === organizationId);
    if (organization) {
      return organization.savePtpAsDraftByDefault;
    }
    return true;
  },
  organizationPtpDeductiblePaymentBillableAsDefault: (state: IOrganizationState) => (organizationId: string) => {
    const organization = state.organizations.find(o => o.id === organizationId);
    if (organization) {
      return organization.ptpDeductiblePaymentBillableAsDefault;
    }
    return true;
  },
  getDestinationById: (state: IOrganizationState, getters: any, rootState: any) => (destinationId: number) => {
    return rootState.destination.destinations.find(d => d.id === destinationId);
  }
};

function passengerToPassengerGroupPassenger(passenger: Passenger) {
  const passengerGroupPassenger: IPassengerGroupPassenger = {
    id: passenger.id,
    name: passenger.name,
    destination: '',
    phone: passenger.phone,
    groups: passenger.groups,
    transportationPermissionCount: passenger.transportationPermissionCount,
    inactive: passenger.inactive,
  };

  return passengerGroupPassenger;
}

const organizationStore = organizationApi.getStore();
(organizationStore as any).namespaced = true;
(organizationStore as any).getters = getters;

organizationStore.mutations.updatePassengerGroupPassengersDestination = (state: IOrganizationState, payload: { passengerId: number, destination: Destination }) => {
  if (state.selectedPassengerGroup) {
    const destinationLabel = payload.destination.alias ? payload.destination.alias : payload.destination.address;
    state.selectedPassengerGroup.passengerGroupPassengers.find(p =>p.id === payload.passengerId)!.destination = destinationLabel;
  }
}

export default organizationStore;
